import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useI18n } from "@/i18n/I18nProvider";
import { api, errorKey } from "@/lib/api";

const EMPTY = { first_name: "", last_name: "", phone: "", password: "", active: true };

const validate = (f, isEdit) => {
  const e = {};
  if (!f.first_name.trim()) e.first_name = "validation.required";
  if (!f.last_name.trim()) e.last_name = "validation.required";
  if (f.phone.replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "").length !== 10) e.phone = "validation.phone";
  if ((!isEdit || f.password) && f.password.length < 8) e.password = "validation.minPassword";
  return e;
};

const Field = ({ id, label, hint, error, t, password, ...props }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    {password
      ? <PasswordInput id={id} testId={`employee-form-${id}`} className="h-12 text-base" {...props} />
      : <Input id={id} data-testid={`employee-form-${id}`} className="h-12 text-base" {...props} />}
    {error ? <p data-testid={`employee-form-${id}-error`} className="text-xs font-medium text-red-600">{t(error)}</p>
      : hint && <p className="text-xs text-gray-500">{hint}</p>}
  </div>
);

export const EmployeeFormDialog = ({ open, onOpenChange, employee, onSaved }) => {
  const { t } = useI18n();
  const isEdit = Boolean(employee);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(employee ? { ...EMPTY, ...employee, password: "" } : EMPTY);
      setErrors({});
    }
  }, [open, employee]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(form, isEdit);
    setErrors(v);
    if (Object.keys(v).length) return;
    setBusy(true);
    const payload = { first_name: form.first_name, last_name: form.last_name, phone: form.phone, active: form.active };
    if (form.password) payload.password = form.password;
    try {
      const r = isEdit ? await api.patch(`/employees/${employee.id}`, payload) : await api.post("/employees", payload);
      toast.success(t(isEdit ? "employees.updated" : "employees.created"));
      onSaved(r.data.employee);
      onOpenChange(false);
    } catch (err) {
      const key = errorKey(err);
      if (key === "errors.phone_in_use") setErrors({ phone: key });
      else toast.error(t(key));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="employee-form-dialog" closeLabel={t("common.close")} className="max-h-[92dvh] w-[calc(100vw-2rem)] max-w-md overflow-y-auto rounded-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#000B45]">{t(isEdit ? "employees.editTitle" : "employees.createTitle")}</DialogTitle>
          <DialogDescription>{t("employees.rateNote")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field t={t} id="first_name" label={t("employees.firstName")} value={form.first_name} onChange={set("first_name")} error={errors.first_name} />
            <Field t={t} id="last_name" label={t("employees.lastName")} value={form.last_name} onChange={set("last_name")} error={errors.last_name} />
          </div>
          <Field t={t} id="phone" type="tel" inputMode="tel" label={t("employees.phone")} hint={t("employees.phoneHint")}
            value={form.phone} onChange={set("phone")} error={errors.phone} />
          <Field t={t} id="password" password autoComplete="new-password"
            label={t(isEdit ? "employees.newPassword" : "employees.password")}
            hint={t(isEdit ? "employees.newPasswordHint" : "employees.passwordHint")}
            value={form.password} onChange={set("password")} error={errors.password} />
          <div className="flex min-h-[48px] items-center justify-between rounded-lg border border-[#E5E7EB] px-4">
            <Label htmlFor="active">{t("employees.activeLabel")}</Label>
            <Switch id="active" data-testid="employee-form-active-switch" checked={form.active}
              onCheckedChange={(v) => setForm({ ...form, active: v })} />
          </div>
          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={() => onOpenChange(false)} data-testid="employee-form-cancel"
              className="min-h-[48px] rounded-lg border border-[#E5E7EB] px-5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50">
              {t("common.cancel")}
            </button>
            <button type="submit" disabled={busy} data-testid="employee-form-submit"
              className="flex min-h-[48px] items-center justify-center gap-2 rounded-lg bg-[#000B45] px-6 text-sm font-bold text-white transition-colors hover:bg-[#000624] disabled:opacity-70">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? t("common.saving") : t(isEdit ? "common.save" : "common.create")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
